import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useTradeShows } from "@/api/queries";
import { Button, FormField, Input, Select } from "@/components/ui";
import type { PickedCity } from "@/recepcion/CityPicker";
import { CityPicker } from "@/recepcion/CityPicker";

import { editionYears } from "./model";
import type { Card, FieldErrors, NewCard } from "./queries";
import { useSaveCard, validateCard } from "./queries";

/**
 * *Captura por formulario*: one card at a time, each field checked as it is
 * filled in.
 *
 * The checking is the server's — `/api/cards/validate/` runs the same
 * serializer that saves — so "ya está en el tarjetero de otro agente" appears
 * while the seller still has the card in hand, not after *Guardar*. A field
 * says nothing until it has been left once: telling someone the name is
 * required while they are typing it is noise.
 */

type Draft = {
  name: string;
  email: string;
  business: string;
  phone: string;
  mobile: string;
  phone2: string;
  job: string;
  trade_show: string;
  edition: string;
  website: string;
  address: string;
  neighborhood: string;
  postal_code: string;
  city: PickedCity | null;
};

type FieldName = keyof Draft;

function draftFrom(card: Card | undefined): Draft {
  return {
    name: card?.name ?? "",
    email: card?.email ?? "",
    business: card?.business ?? "",
    phone: card?.phone ?? "",
    mobile: card?.mobile ?? "",
    phone2: card?.phone2 ?? "",
    job: card?.job ?? "",
    trade_show: card?.trade_show ? String(card.trade_show) : "",
    edition: card?.edition ? String(card.edition) : "",
    website: card?.website ?? "",
    address: card?.address ?? "",
    neighborhood: card?.neighborhood ?? "",
    postal_code: card?.postal_code ?? "",
    city:
      card?.city && card.city_name ? { id: card.city, label: card.city_name } : null,
  };
}

function toBody(draft: Draft): NewCard {
  return {
    name: draft.name.trim(),
    email: draft.email.trim(),
    business: draft.business.trim(),
    phone: draft.phone.trim(),
    mobile: draft.mobile.trim(),
    phone2: draft.phone2.trim(),
    job: draft.job.trim(),
    trade_show: draft.trade_show ? Number(draft.trade_show) : null,
    edition: draft.edition ? Number(draft.edition) : null,
    website: draft.website.trim(),
    address: draft.address.trim(),
    neighborhood: draft.neighborhood.trim(),
    postal_code: draft.postal_code.trim(),
    city: draft.city?.id ?? null,
  };
}

/** How long typing has to pause before the draft is checked. */
const SETTLE_MS = 400;

export function CardForm({
  card,
  onSaved,
  onCancel,
}: {
  /** Pass to edit; without it the form captures new cards one after another. */
  card?: Card;
  onSaved?: (card: Card) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(card));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<ReadonlySet<FieldName>>(() => new Set());
  const [submitted, setSubmitted] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const save = useSaveCard(card?.id);
  const tradeShows = useTradeShows();
  const form = useRef<HTMLFormElement>(null);
  // Answers can arrive out of order; only the newest draft's may be shown.
  const latest = useRef(0);

  useEffect(() => {
    const request = ++latest.current;
    const timer = setTimeout(() => {
      setChecking(true);
      validateCard(toBody(draft), card?.id)
        .then((found) => {
          if (request === latest.current) setErrors(found);
        })
        // A check that could not run says nothing; the save will.
        .catch(() => undefined)
        .finally(() => {
          if (request === latest.current) setChecking(false);
        });
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [draft, card?.id]);

  function set<K extends FieldName>(key: K, value: Draft[K]) {
    setSaved(null);
    setDraft((previous) => ({ ...previous, [key]: value }));
  }

  function leave(key: FieldName) {
    setTouched((previous) =>
      previous.has(key) ? previous : new Set(previous).add(key),
    );
  }

  function errorFor(key: FieldName): string | undefined {
    if (!submitted && !touched.has(key)) return undefined;
    return errors[key]?.[0];
  }

  async function submit() {
    setSubmitted(true);
    setFailure(null);
    const body = toBody(draft);
    let found: FieldErrors;
    try {
      found = await validateCard(body, card?.id);
    } catch (problem) {
      setFailure(problem instanceof Error ? problem.message : t("errors.saveCard"));
      return;
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    try {
      const result = await save.mutateAsync(body);
      onSaved?.(result);
      if (!card) {
        // The next card at the same show is the usual next step, so the show
        // and its edition stay.
        setSaved(result.name);
        setDraft({
          ...draftFrom(undefined),
          trade_show: draft.trade_show,
          edition: draft.edition,
        });
        setTouched(new Set());
        setSubmitted(false);
        // `Input` takes no ref; the field the dialog focuses is marked instead.
        form.current?.querySelector<HTMLInputElement>("[data-autofocus]")?.focus();
      }
    } catch (problem) {
      setFailure(problem instanceof Error ? problem.message : t("errors.saveCard"));
    }
  }

  const shows = tradeShows.data?.results ?? [];
  const years = editionYears();
  const nonFieldError = submitted ? errors.non_field_errors?.[0] : undefined;

  function text(
    key: Exclude<FieldName, "city" | "trade_show" | "edition">,
    props: { type?: string; maxLength: number; inputMode?: "tel" | "email" | "url" } = {
      maxLength: 200,
    },
  ) {
    const error = errorFor(key);
    return (
      <Field label={t(`cards.fields.${key}`)} error={error}>
        {(id, errorId) => (
          <Input
            id={id}
            value={draft[key]}
            type={props.type ?? "text"}
            maxLength={props.maxLength}
            inputMode={props.inputMode}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className={error ? "border-brand" : ""}
            onChange={(event) => set(key, event.target.value)}
            onBlur={() => leave(key)}
            {...(key === "business" ? { "data-autofocus": "" } : {})}
          />
        )}
      </Field>
    );
  }

  return (
    <form
      ref={form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {saved ? (
        <p
          role="status"
          className="mb-4 border-2 border-rule bg-panel px-3 py-2 text-sm"
        >
          {t("cards.form.saved", { name: saved })}
        </p>
      ) : null}
      {failure || nonFieldError ? (
        <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
          {failure ?? nonFieldError}
        </p>
      ) : null}

      <div className="grid gap-5 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3">
        {text("business", { maxLength: 250 })}
        {text("name", { maxLength: 200 })}
        {text("email", { type: "email", maxLength: 200, inputMode: "email" })}
        {text("phone", { type: "tel", maxLength: 30, inputMode: "tel" })}
        {text("mobile", { type: "tel", maxLength: 30, inputMode: "tel" })}
        {text("phone2", { type: "tel", maxLength: 30, inputMode: "tel" })}
        {text("job", { maxLength: 100 })}

        <Field label={t("cards.fields.trade_show")} error={errorFor("trade_show")}>
          {(id) => (
            <Select
              id={id}
              value={draft.trade_show}
              onChange={(event) => set("trade_show", event.target.value)}
              onBlur={() => leave("trade_show")}
            >
              <option value="">{t("cards.form.noShow")}</option>
              {/* A retired show still names the card being edited. */}
              {card?.trade_show &&
              !shows.some((show) => show.id === card.trade_show) ? (
                <option value={String(card.trade_show)}>{card.trade_show_name}</option>
              ) : null}
              {shows.map((show) => (
                <option key={show.id} value={String(show.id)}>
                  {show.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={t("cards.fields.edition")} error={errorFor("edition")}>
          {(id) => (
            <Select
              id={id}
              value={draft.edition}
              onChange={(event) => set("edition", event.target.value)}
              onBlur={() => leave("edition")}
            >
              <option value="">{t("cards.form.noEdition")}</option>
              {draft.edition && !years.includes(Number(draft.edition)) ? (
                <option value={draft.edition}>{draft.edition}</option>
              ) : null}
              {years.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {text("website", { type: "url", maxLength: 100, inputMode: "url" })}
        {text("address", { maxLength: 200 })}
        {text("neighborhood", { maxLength: 100 })}
        {text("postal_code", { maxLength: 10 })}

        <div>
          <CityPicker
            label={t("cards.fields.city")}
            value={draft.city}
            onChange={(city) => {
              set("city", city);
              leave("city");
            }}
          />
          {errorFor("city") ? (
            <p className="mt-1.5 mb-0 text-xs text-brand-700">{errorFor("city")}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {save.isPending
            ? t("cards.form.saving")
            : card
              ? t("cards.form.saveChanges")
              : t("cards.form.save")}
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        ) : null}
        <span className="text-xs text-mute-600" aria-live="polite">
          {checking ? t("cards.form.checking") : null}
        </span>
      </div>
    </form>
  );
}

/** `FormField` with the field's error under it, wired for a screen reader. */
function Field({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: (id: string, errorId: string) => ReactNode;
}) {
  return (
    <FormField label={label}>
      {(id) => (
        <>
          {children(id, `${id}-error`)}
          {error ? (
            <p id={`${id}-error`} className="mt-1.5 mb-0 text-xs text-brand-700">
              {error}
            </p>
          ) : null}
        </>
      )}
    </FormField>
  );
}
